<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, provide } from 'vue'
import { RouterView, RouterLink, useRoute, useRouter } from 'vue-router'
import ToastContainer from '@/components/common/ToastContainer.vue'
import ErrorBoundary from '@/components/common/ErrorBoundary.vue'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'
import { useDashboardStore } from '@/stores/dashboard'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from '@/i18n'

const mobileMenuOpen = ref(false)
const sidebarCollapsed = ref(false)
const route = useRoute()
const router = useRouter()
const currentTime = ref('')
const dashboardStore = useDashboardStore()
const auth = useAuthStore()
const { locale, t } = useI18n()

// หน้า public (login) ใช้ layout เปล่า ไม่มี sidebar/header
const isPublicRoute = computed(() => route.meta.public === true)

const handleLogout = () => {
  auth.logout()
  router.replace({ name: 'login' })
}

// Toggle sidebar collapse
const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

// Provide sidebar state to child components (for chart resize)
provide('sidebarCollapsed', sidebarCollapsed)

// Badge counts for sidebar
const alarmBadgeCount = computed(() => {
  // รวม alarms + warnings
  const alarms = dashboardStore.computedSummary.total_alarms || 0
  const warnings = dashboardStore.computedSummary.warning_stations || 0
  return alarms + warnings
})

// ---------------------------------------------------------------------------
// เดิม dashboardStore ถูกสั่งโหลดจาก DashboardView ที่เดียว ส่วน sidebar แค่
// อ่านค่าไปแสดง — เปิดหน้าอื่นตรงๆ (เช่น /alarms จาก bookmark หรือ refresh
// ค้างอยู่หน้า History) store เลยยังว่าง badge ไม่ขึ้นจนกว่าจะแวะ Dashboard
// ก่อน และพอออกจาก Dashboard polling ก็หยุด ตัวเลขเลยค้างไม่อัปเดตต่อ
//
// sidebar เป็นเจ้าของ badge ก็ควรโหลดข้อมูลที่ตัวเองต้องใช้เอง
//
// เรียกแค่ fetchStationStatus() ไม่ใช่ refresh() ทั้งก้อน — badge ใช้แค่
// stations_alert/stations_warning จาก /station-status-summary/ ส่วน station
// list กับ active alarm log ที่ refresh() ดึงมาด้วยเป็นของหน้า Dashboard
// เท่านั้น ดึงมาทุกหน้าคือเปลืองเปล่า
// ---------------------------------------------------------------------------
const BADGE_POLL_MS = 60000
let badgeInterval: number | undefined

const refreshBadge = async () => {
  // ยังไม่ login อย่ายิง ไม่งั้นโดน 401 แล้ว interceptor เด้งออกหน้า login
  if (!auth.isAuthenticated) return
  try {
    await dashboardStore.fetchStationStatus()
  } catch {
    // badge เป็นข้อมูลเสริม ล้มแล้วไม่ควรไปรบกวนหน้าที่ผู้ใช้กำลังดูอยู่
  }
}

// เพิ่ง login เสร็จ (หรือ token กลับมา) ให้ดึงทันทีไม่ต้องรอครบรอบ poll
watch(() => auth.isAuthenticated, (loggedIn) => {
  if (loggedIn) refreshBadge()
})

// ป้ายใต้ชื่อผู้ใช้ — superuser ของ Django ได้สิทธิ์ทุกอย่างโดยไม่ต้องอยู่
// group ไหนเลย (ดู RolePermissionView) roles เลยว่างแล้วตกไปที่ 'User'
// ซึ่งบอกผิดว่าเป็นผู้ใช้ทั่วไป — เช็ค is_superuser ก่อนเสมอ
const roleLabel = computed(() => {
  if (auth.isSuperuser) return t('Administrator')
  return auth.roles[0] || t('User')
})

// permission: null = ทุกคนที่ login เห็นได้ / ถ้าระบุ code ต้องมีสิทธิ์นั้นถึงจะเห็น
// (code ตรงกับ AppPermission ใน backend: MANAGE_MASTER/VIEW/EXPORT/MANAGE_USER)
const navLinks = computed(() => [
  { to: '/', label: t('Dashboard'), icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z', badge: null, permission: null },
  { to: '/maps', label: t('Maps'), icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7', badge: null, permission: null },
  { to: '/history', label: t('History'), icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', badge: null, permission: null },
  { to: '/alarms', label: t('Alarms'), icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', badge: 'alarm', permission: null },
  { to: '/notifications', label: t('Notification'), icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4', badge: null, permission: null },
  { to: '/settings/thresholds', label: t('Thresholds'), icon: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4', badge: null, permission: null },
  { to: '/settings/stations', label: t('Settings'), icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z', badge: null, permission: 'MANAGE_MASTER' },
])

// เมนูที่ user คนนี้เห็นได้ตามสิทธิ์ — ใช้ทั้ง sidebar (desktop) และ mobile
const visibleNavLinks = computed(() =>
  navLinks.value.filter((link) => !link.permission || auth.hasPermission(link.permission))
)

// Get badge count for a nav link
const getBadgeCount = (badgeType: string | null) => {
  if (badgeType === 'alarm') return alarmBadgeCount.value
  return 0
}

const isActiveRoute = (to: string) => {
  if (to === '/') return route.path === '/'
  return route.path.startsWith(to)
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

let timeInterval: number

const updateTime = () => {
  const now = new Date()
  currentTime.value = now.toLocaleTimeString(locale.value === 'th' ? 'th-TH' : 'en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

watch(locale, updateTime)

onMounted(() => {
  updateTime()
  timeInterval = window.setInterval(updateTime, 1000)
  // badge ต้องมีเลขตั้งแต่หน้าแรกที่เปิด ไม่ว่าจะเป็นหน้าไหน
  refreshBadge()
  badgeInterval = window.setInterval(refreshBadge, BADGE_POLL_MS)
})

onUnmounted(() => {
  clearInterval(timeInterval)
  clearInterval(badgeInterval)
})
</script>

<template>
  <!-- Public (login) — layout เปล่า ไม่มี sidebar -->
  <RouterView v-if="isPublicRoute" />

  <div v-else class="min-h-screen bg-gray-100 flex">
    <!-- Sidebar - Desktop -->
    <aside
      class="hidden md:flex md:flex-col bg-white shadow-lg fixed h-full z-30 transition-all duration-300"
      :class="sidebarCollapsed ? 'w-16' : 'w-56'"
    >
      <!-- Logo -->
      <!-- ตอนหุบ ลด padding ข้างลงเพื่อให้โลโก้โตได้ — p-4 เดิมกินไป 32px จาก
           ความกว้าง 64px ทำให้รูปใหญ่เกิน w-8 ไม่ได้ (preflight บีบด้วย max-width) -->
      <div class="border-b border-gray-100" :class="sidebarCollapsed ? 'px-1 py-3' : 'p-4'">
        <div class="flex items-center" :class="sidebarCollapsed ? 'justify-center' : ''">
          <!-- ตอนหุบ sidebar เหลือที่ว่างแค่ ~56px — logo.png เป็นแบบแนวนอน 3:1
               ย่อลงมาแล้วเตี้ยจนอ่านไม่ออก เลยสลับไปใช้ favicon.png
               ที่เป็นจัตุรัสแทน (โลโก้เดียวกัน คนละ lockup) -->
          <img
            v-if="sidebarCollapsed"
            src="/favicon.png"
            alt="NongmOR"
            class="w-14 h-14 flex-shrink-0 object-contain"
          />
          <img v-else src="/logo.png" alt="NongmOR" class="h-auto w-auto" />
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 p-2 space-y-1">
        <RouterLink
          v-for="link in visibleNavLinks"
          :key="link.to"
          :to="link.to"
          class="flex items-center rounded-lg text-sm font-medium transition-colors relative group"
          :class="[
            sidebarCollapsed ? 'justify-center p-3' : 'px-4 py-3',
            isActiveRoute(link.to)
              ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
          ]"
          :title="sidebarCollapsed ? link.label : ''"
        >
          <svg class="w-5 h-5 flex-shrink-0" :class="sidebarCollapsed ? '' : 'mr-3'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="link.icon" />
          </svg>
          <span v-if="!sidebarCollapsed">{{ link.label }}</span>
          <!-- Badge -->
          <span
            v-if="link.badge && getBadgeCount(link.badge) > 0"
            class="bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            :class="sidebarCollapsed ? 'absolute -top-1 -right-1' : 'ml-auto'"
          >
            {{ getBadgeCount(link.badge) > 99 ? '99+' : getBadgeCount(link.badge) }}
          </span>
          <!-- Tooltip when collapsed -->
          <div
            v-if="sidebarCollapsed"
            class="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity whitespace-nowrap z-50"
          >
            {{ link.label }}
          </div>
        </RouterLink>
      </nav>

      <!-- Toggle Button -->
      <button
        @click="toggleSidebar"
        class="p-3 border-t border-gray-100 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors flex items-center"
        :class="sidebarCollapsed ? 'justify-center' : 'justify-end'"
        :title="sidebarCollapsed ? t('Expand sidebar') : t('Collapse sidebar')"
      >
        <svg
          class="w-5 h-5 transition-transform duration-300"
          :class="sidebarCollapsed ? 'rotate-180' : ''"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
      </button>

      <div class="flex justify-center border-t border-gray-100 p-2">
        <LanguageSwitcher :compact="sidebarCollapsed" />
      </div>

      <!-- User + Logout -->
      <div class="p-2 border-t border-gray-100">
        <div v-if="!sidebarCollapsed && auth.user" class="flex items-center px-2 py-1.5 mb-1">
          <div class="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold flex-shrink-0">
            {{ (auth.user.username || '?').charAt(0).toUpperCase() }}
          </div>
          <div class="ml-2 min-w-0">
            <p class="text-sm font-medium text-gray-800 truncate">{{ auth.user.username }}</p>
            <p class="text-xs text-gray-400 truncate">{{ roleLabel }}</p>
          </div>
        </div>
        <button
          @click="handleLogout"
          class="flex items-center w-full rounded-lg text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          :class="sidebarCollapsed ? 'justify-center p-3' : 'px-4 py-2.5'"
          :title="sidebarCollapsed ? t('Logout') : ''"
        >
          <svg class="w-5 h-5 flex-shrink-0" :class="sidebarCollapsed ? '' : 'mr-3'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span v-if="!sidebarCollapsed">{{ t('Logout') }}</span>
        </button>
      </div>

      <!-- Footer -->
      <div class="p-3 border-t border-gray-100" :class="sidebarCollapsed ? 'text-center' : ''">
        <div class="flex items-center text-gray-400 text-sm mb-1" :class="sidebarCollapsed ? 'justify-center' : ''">
          <svg v-if="!sidebarCollapsed" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span :class="sidebarCollapsed ? 'text-xs' : ''">{{ sidebarCollapsed ? currentTime.slice(0, 5) : currentTime }}</span>
        </div>
        <p v-if="!sidebarCollapsed" class="text-xs text-gray-300">v2.0.1</p>
      </div>
    </aside>

    <!-- Mobile Header -->
    <header class="md:hidden fixed top-0 left-0 right-0 bg-white shadow-sm z-40">
      <div class="flex items-center justify-between px-4 h-14">
        <div class="flex items-center">
          <img src="/logo.png" alt="NongmOR" class="h-auto w-auto" />
        </div>
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
          :aria-label="mobileMenuOpen ? t('Close menu') : t('Open menu')"
        >
          <svg v-if="!mobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Mobile Navigation -->
      <Transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div v-if="mobileMenuOpen" class="bg-white border-t">
          <nav class="p-2 space-y-1">
            <RouterLink
              v-for="link in visibleNavLinks"
              :key="link.to"
              :to="link.to"
              @click="closeMobileMenu"
              class="flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors"
              :class="isActiveRoute(link.to)
                ? 'bg-blue-50 text-blue-600'
                : 'text-gray-600 hover:bg-gray-50'"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="link.icon" />
              </svg>
              {{ link.label }}
              <!-- Badge -->
              <span
                v-if="link.badge && getBadgeCount(link.badge) > 0"
                class="ml-auto bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5"
              >
                {{ getBadgeCount(link.badge) > 99 ? '99+' : getBadgeCount(link.badge) }}
              </span>
            </RouterLink>
            <div class="flex justify-end border-t border-gray-100 px-4 pt-3">
              <LanguageSwitcher />
            </div>
          </nav>
        </div>
      </Transition>
    </header>

    <!-- Main Content -->
    <!-- min-w-0 จำเป็น: main เป็น flex item ซึ่ง default min-width:auto แปลว่า
         มันจะไม่ยอมหดต่ำกว่าความกว้างของเนื้อหาข้างใน  <canvas> ของ chart.js
         ถูกเซ็ต width เป็น px จริง พอกาง sidebar (w-16 -> w-56) main ควรหดลง
         160px แต่หดไม่ได้เพราะติด canvas ตัวเดิม เลยดันทะลุจอเป็น scroll แนวนอน -->
    <main
      class="flex-1 min-w-0 pt-14 md:pt-0 transition-all duration-300"
      :class="sidebarCollapsed ? 'md:ml-16' : 'md:ml-56'"
    >
      <div class="p-4 md:p-6">
        <ErrorBoundary>
          <RouterView />
        </ErrorBoundary>
      </div>
    </main>

  </div>

  <!-- Toast Notifications (render ทั้งหน้า login และในแอป) -->
  <ToastContainer />
</template>
